import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  MessageSquare,
  Send,
  Clock,
  Archive,
  Ban,
  BarChart3,
  Settings,
  ChevronLeft,
  FileText,
  Users,
  FileEdit
} from 'lucide-react';

/**
 * SMSLayout - Layout dédié pour le module SMS
 * Interface distincte avec navigation propre pour les fonctionnalités SMS
 */
const SMSLayout = ({ children }) => {
  const { user, isSuperAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();

  const smsNavItems = [
    {
      path: '/sms/mailing',
      label: 'Nouvelle campagne',
      icon: Send,
      description: 'Créer et envoyer une campagne'
    },
    {
      path: '/sms/queue',
      label: "File d'attente",
      icon: Clock,
      description: 'Campagnes programmées'
    },
    {
      path: '/sms/templates',
      label: 'Messages prédéfinis',
      icon: FileEdit,
      description: 'Modèles de messages'
    },
    {
      path: '/sms/data-import',
      label: 'Import données',
      icon: FileText,
      description: 'Importer et nettoyer'
    },
    {
      path: '/sms/archives',
      label: 'Archives',
      icon: Archive,
      description: 'Historique des envois'
    },
    {
      path: '/sms/contacts',
      label: 'Contacts',
      icon: Users,
      description: 'Gérer vos listes'
    },
    {
      path: '/sms/blacklist',
      label: 'Blacklist',
      icon: Ban,
      description: 'Numéros bloqués'
    },
    {
      path: '/sms/statistics',
      label: 'Statistiques',
      icon: BarChart3,
      description: 'Performances'
    },
  ];

  // Add settings for admin
  if (isSuperAdmin) {
    smsNavItems.push({
      path: '/sms/settings',
      label: 'Configuration',
      icon: Settings,
      description: 'Paramètres SMS'
    });
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[hsl(220,14%,10%)]' : 'bg-slate-50'}`}>
      {/* Top Header */}
      <header className={`sticky top-0 z-50 border-b ${isDarkMode ? 'bg-[hsl(220,14%,12%)] border-[hsl(220,14%,20%)]' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            {/* Back to main app */}
            <NavLink 
              to="/dashboard"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-[hsl(220,14%,18%)] text-slate-400 hover:text-white' 
                  : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Retour</span>
            </NavLink>
            
            <div className="h-6 w-px bg-slate-300" />
            
            {/* SMS Module Title */}
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                <MessageSquare className={`h-6 w-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              </div>
              <div>
                <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  SMS Mailing
                </h1>
                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Gestion des campagnes SMS
                </p>
              </div>
            </div>
          </div>

          {/* User info / Provider status */}
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
              isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
            }`}>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Provider connecté
            </div>
          </div>
        </div>

        {/* Sub Navigation */}
        <nav className="px-6 overflow-x-auto">
          <div className="flex gap-1 pb-px">
            {smsNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? isDarkMode 
                        ? 'border-indigo-500 text-indigo-400' 
                        : 'border-indigo-600 text-indigo-600'
                      : isDarkMode
                        ? 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                        : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default SMSLayout;
